import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import cx from 'classnames';
import { FadeLoader } from 'react-spinners';
import get from 'lodash/get';
import mediumZoom from 'medium-zoom'
import ReactCrop from '../lib/react-image-crop/lib/ReactCrop';
import '../lib/react-image-crop/dist/ReactCrop.css';
import BehindLayersOverlay from './BehindLayersOverlay';
import MaterialIcon from './MaterialIcon';

export default class Thumbs extends Component {
    static defaultProps = {
        images: [],
        processingId: '1',
        addImages: () => Promise.resolve(),
        deleteImage: () => Promise.resolve(),
        deleteSelected: () => Promise.resolve(),
        deleteAll: () => Promise.resolve(),
        onSelectionChanged: () => Promise.reslove(),
        onCropsChanged: () => Promise.reslove(),
        behindSubjectEnabled: false,
        behindLayers: [],
        focusedImageId: null,
        onLayerDrag: () => {},
    };

    static propTypes = {
        images: PropTypes.array,
        processingId: PropTypes.string,
        addImages: PropTypes.func,
        deleteImage: PropTypes.func,
        deleteSelected: PropTypes.func,
        deleteAll: PropTypes.func,
        onSelectionChanged: PropTypes.func,
        onCropsChanged: PropTypes.func,
        behindSubjectEnabled: PropTypes.bool,
        behindLayers: PropTypes.array,
        focusedImageId: PropTypes.string,
        onLayerDrag: PropTypes.func,
    };

    fileInputRef = React.createRef();
    previewImageElements = {};

    state = {
        selected: [],
        cropped: [],
        crops: {},
    };

    render() {
        const { selected } = this.state;
        const { images } = this.props;
        const hasSelected = selected.length > 0;

        return (
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex shrink-0 flex-col items-center gap-2 border-b border-border-subtle bg-white px-4 py-3 desk:flex-row desk:items-center">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        <button
                            type="button"
                            className="rounded-md border border-border-subtle px-3 py-1.5 text-label-md text-on-surface transition-colors hover:bg-surface"
                            onClick={ this.selectAll }
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            className="rounded-md border border-border-subtle px-3 py-1.5 text-label-md text-on-surface transition-colors hover:bg-surface"
                            onClick={ this.selectNone }
                        >
                            Select None
                        </button>
                        <button
                            type="button"
                            className="rounded-md border border-border-subtle px-3 py-1.5 text-label-md text-on-surface transition-colors hover:bg-surface"
                            onClick={ this.invertSelection }
                        >
                            Invert
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 desk:ml-auto">
                        <input
                            ref={this.fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={this.onAddFilesSelected}
                        />
                        <button
                            type="button"
                            className="rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-label-md text-primary transition-colors hover:bg-primary/10"
                            onClick={this.openAddFiles}
                        >
                            Add Images
                        </button>
                        <button
                            type="button"
                            className="rounded-md border border-danger/30 px-3 py-1.5 text-label-md text-danger transition-colors hover:bg-danger/5 disabled:cursor-not-allowed disabled:opacity-40"
                            onClick={this.deleteSelected}
                            disabled={!hasSelected}
                        >
                            Delete Selected
                        </button>
                        <button
                            type="button"
                            className="rounded-md border border-danger/30 px-3 py-1.5 text-label-md text-danger transition-colors hover:bg-danger/5"
                            onClick={ this.props.deleteAll }
                        >
                            Delete All
                        </button>
                    </div>
                </div>
                <div className={cx('flex min-h-0 flex-1 custom-scrollbar overflow-auto', {
                    'flex-col desk:flex-row': hasSelected,
                    'flex-col': !hasSelected,
                })}>
                    <div className={cx('min-h-0 custom-scrollbar p-4', {
                        'shrink-0 overflow-x-auto overflow-y-hidden desk:w-56 desk:shrink-0 desk:overflow-y-auto desk:overflow-x-hidden desk:border-r desk:border-border-subtle': hasSelected,
                        'overflow-x-auto overflow-y-hidden desk:overflow-auto': !hasSelected,
                    })}>
                        <div className={cx(
                            'flex snap-x snap-mandatory gap-2 pb-1',
                            'desk:grid desk:snap-none desk:gap-3 desk:pb-0',
                            hasSelected ? 'desk:grid-cols-1' : 'desk:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5',
                        )}>
                            { images.map(this.renderThumb) }
                        </div>
                    </div>
                    { hasSelected && (
                        <div className="flex min-h-0 min-w-0 flex-1 flex-col custom-scrollbar overflow-auto p-4">
                            { this.renderLargeImages() }
                        </div>
                    ) }
                </div>
            </div>
        );
    }

    renderLargeImages() {
        const { images } = this.props;
        const { selected } = this.state;
        const selectedImages = images.filter(image => selected.includes(image.id));

        if (selectedImages.length > 0) {
            return (
                <div className="flex flex-col gap-6">
                    { selectedImages.map(this.renderImage) }
                </div>
            );
        } else {
            return null;
        }
    }

    renderImage = (image) => {
        const { cropped, crops } = this.state;
        const {
            behindSubjectEnabled,
            behindLayers,
            focusedImageId,
            onLayerDrag,
        } = this.props;
        const isCropped = cropped.includes(image.id);
        const src = get(image, 'previewFull.src') || get(image, 'fullSize.src');
        const showBehindOverlay = behindSubjectEnabled
            && focusedImageId === image.id
            && behindLayers.length > 0
            && !isCropped;

        return (
            <div key={image.id} className="flex flex-col gap-3">
                <div
                    className="relative flex items-center justify-center overflow-hidden rounded-lg border border-border-subtle bg-surface-dim"
                    ref={element => { this.imageWrapper = element; }}
                >
                    {
                        !isCropped && (
                            <img
                                ref={element => { this.previewImageElements[image.id] = element; }}
                                className="zoomable max-h-[60vh] max-w-full object-contain"
                                onClick={() => this.zoomImage(image.id)}
                                alt={image.name}
                                src={src}
                            />
                        )
                    }
                    {
                        showBehindOverlay && (
                            <BehindLayersOverlay
                                enabled={behindSubjectEnabled}
                                layers={behindLayers}
                                imageWidth={image.size.width}
                                imageHeight={image.size.height}
                                getImageElement={() => this.previewImageElements[image.id]}
                                onLayerDrag={onLayerDrag}
                            />
                        )
                    }
                    {
                        isCropped &&
                        <ReactCrop
                            src={src}
                            crop={crops[image.id]}
                            keepSelection
                            onChange={(crop) => this.changeCrop(image.id, crop)}
                        />
                    }
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        title="Crop"
                        className={cx(
                            'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                            isCropped
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border-subtle text-secondary hover:border-primary/50 hover:text-primary'
                        )}
                        onClick={() => this.toggleCrop(image.id) }
                    >
                        <MaterialIcon name="crop" className="h-5 w-5" />
                    </button>
                    {
                        isCropped &&
                        <button
                            type="button"
                            title="Lock aspect ratio"
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border-subtle text-secondary transition-colors hover:border-primary/50 hover:text-primary"
                            onClick={() => this.toggleCropRationLock(image.id)}
                        >
                            <MaterialIcon
                                name={ !isNil(crops[image.id].aspect) ? 'lock' : 'lock_open' }
                                className="h-5 w-5"
                            />
                        </button>
                    }
                    <button
                        type="button"
                        title="Zoom"
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border-subtle text-secondary transition-colors hover:border-primary/50 hover:text-primary"
                        onClick={() => this.zoomImage(image.id)}
                    >
                        <MaterialIcon name="zoom_in" className="h-5 w-5" />
                    </button>
                    <span className="ml-2 truncate text-label-md text-secondary">{ image.name }</span>
                </div>
            </div>
        );
    };

    renderThumb = (image) => {
        const { selected } = this.state;
        const { processingId } = this.props;
        const isSelected = selected.includes(image.id);

        return (
            <div
                key={image.id}
                className={cx(
                    'group relative w-[5.25rem] shrink-0 snap-start rounded-lg border-2 bg-white transition-all desk:w-full',
                    isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border-subtle hover:border-primary/40'
                )}
            >
                <div className="relative cursor-pointer overflow-hidden rounded-md" onClick={() => this.toggleSelected(image.id)}>
                    <div className="flex aspect-square w-full items-center justify-center bg-surface-dim">
                        <img
                            alt={image.name}
                            className="max-h-full max-w-full object-contain"
                            src={image.previewThumb.src}
                        />
                    </div>
                    { isSelected && (
                        <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white desk:right-1.5 desk:top-1.5 desk:h-5 desk:w-5">
                            <MaterialIcon name="check" className="h-3 w-3 desk:h-4 desk:w-4" />
                        </span>
                    ) }
                    <div className="hidden px-2 py-1.5 desk:block">
                        <p className="truncate text-label-sm text-on-surface" title={image.name}>{ image.name }</p>
                        <p className="text-label-sm text-secondary" title={`${image.fileSize.bytes} Bytes`}>
                            { `${image.size.width}×${image.size.height}, ${image.fileSize.readable}` }
                        </p>
                    </div>
                    {
                        (processingId === image.id) &&
                        <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                            <FadeLoader loading color="#00B3E0"/>
                        </div>
                    }
                </div>
                <button
                    type="button"
                    title="Delete"
                    className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-border-subtle bg-white text-secondary opacity-100 shadow-sm transition-opacity desk:-right-1.5 desk:-top-1.5 desk:h-6 desk:w-6 desk:opacity-0 desk:group-hover:opacity-100 hover:border-danger hover:text-danger"
                    onClick={ (e) => { e.stopPropagation(); this.deleteImage(image.id); } }
                >
                    <MaterialIcon name="close" className="h-4 w-4" />
                </button>
            </div>
        );
    };

    zoomImage = (id) => {
        const image = this.props.images.find(image => image.id === id);
        const img = new Image();
        img.classList.add('zoom');
        img.onload = () => {
            this.imageWrapper.appendChild(img);
            const zoom = mediumZoom(img, {
                background: 'rgba(20, 20, 20, .85)'
            });
            zoom.on('close', () => this.imageWrapper.removeChild(img));
            zoom.open();
        }
        img.src = get(image, 'previewFull.src') || get(image, 'fullSize.src');
    };

    toggleCropRationLock = (id) => {
        const crop = this.state.crops[id];
        const image = this.props.images[id];
        if (isNil(crop.aspect)) {
            const newCrop = {
                aspect: image.size.width / image.size.height,
                x: 100/5,
                y: 100/5,
                width: 60,
                height: 60,
            };
            this.setState({
                crops: {
                    ...this.state.crops,
                    [id]: newCrop,
                }
            }, this.onCropsChanged);
        } else {
            this.setState({
                crops: {
                    ...this.state.crops,
                    [id]: {
                        ...crop,
                        aspect: undefined,
                    }
                }
            }, this.onCropsChanged);
        }
    };

    changeCrop = (id, crop) => {
        this.setState({
            crops: {
                ...this.state.crops,
                [id]: crop,
            }
        }, this.onCropsChanged);
    };

    openAddFiles = () => {
        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = '';
            this.fileInputRef.current.click();
        }
    };

    onAddFilesSelected = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            this.props.addImages(files);
        }
    };

    deleteSelected = () => {
        const { selected, cropped, crops } = this.state;
        if (selected.length === 0) {
            return;
        }
        const idSet = new Set(selected);
        const remainingCrops = { ...crops };
        selected.forEach(id => delete remainingCrops[id]);

        this.setState({
            selected: [],
            cropped: cropped.filter(id => !idSet.has(id)),
            crops: remainingCrops,
        }, () => {
            this.props.deleteSelected(selected);
            this.onSelectionChanged();
        });
    };

    deleteImage = (id) => {
        const { selected, cropped, crops } = this.state;
        const remainingCrops = { ...crops };
        delete remainingCrops[id];

        this.setState({
            selected: selected.filter(sid => sid !== id),
            cropped: cropped.filter(cid => cid !== id),
            crops: remainingCrops,
        }, () => {
            this.props.deleteImage(id);
            this.onSelectionChanged();
            this.onCropsChanged();
        });
    };

    toggleCrop = (id) => {
        const { cropped } = this.state;
        const { images } = this.props;
        const image = images.find(image => image.id === id);
        if (cropped.includes(id)) {
            const index = cropped.indexOf(id);
            this.setState({
                cropped: [...cropped.splice(0, index), ...cropped.splice(index + 1)],
            }, this.onCropsChanged);
        } else {
            const crop = this.state.crops[id];
            const w = image.size.width;
            const h = image.size.height;
            const newCrop = {
                aspect: w / h,
                x: 100/5,
                y: 100/5,
                width: 60,
                height: 60,
            };
            this.setState({
                cropped: [...cropped, id],
                crops: {
                    ...this.state.crops,
                    [id]: isEmpty(crop) ? newCrop : crop,
                }
            }, this.onCropsChanged);
        }
    };

    toggleSelected = (id) => {
        const { selected } = this.state;
        if (selected.includes(id)) {
            const index = selected.indexOf(id);
            this.setState({
                selected: [...selected.splice(0, index), ...selected.splice(index + 1)]
            }, this.onSelectionChanged);
        } else {
            this.setState({
                selected: [...selected, id]
            }, this.onSelectionChanged);
        }
    };

    onCropsChanged = () => {
        const { cropped, crops } = this.state;
        this.props.onCropsChanged(cropped, crops);
    };

    onSelectionChanged = () => {
        const { selected } = this.state;
        this.props.onSelectionChanged(selected);
    };

    selectAll = () => {
        const { images } = this.props;
        this.setState({
            selected: images.map(image => image.id),
        }, this.onSelectionChanged);
    };

    selectNone = () => {
        this.setState({
            selected: [],
        }, this.onSelectionChanged);
    };

    invertSelection = () => {
        const { images } = this.props;
        this.setState({
            selected: images.filter(image => !this.state.selected.includes(image.id)).map(image => image.id),
        }, this.onSelectionChanged);
    };
}
